import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { MediaGallery } from "@/components/MediaGallery";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Share2, ArrowLeft } from "lucide-react";
import { fetchProduct } from "@/lib/api";
import { buildWhatsAppLink, enquireMessage } from "@/lib/whatsapp";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    fetchProduct(id)
      .then((data) => {
        if (!cancelled) setProduct(data);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleShare = async () => {
    const url = window.location.href;
    const shareText = product ? `Check out ${product.name} at Vini Gems And Jewellers` : "";

    if (navigator.share) {
      try {
        await navigator.share({ title: product?.name, text: shareText, url });
      } catch {
        // User cancelled the native share sheet — no action needed.
      }
    } else {
      // Desktop fallback: share directly via WhatsApp with the link included.
      window.open(buildWhatsAppLink(`${shareText}\n${url}`), "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="pt-32 pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/all-items"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8"
            data-testid="product-back-link"
          >
            <ArrowLeft className="h-4 w-4" /> Back to All Items
          </Link>

          {loading ? (
            <div className="grid md:grid-cols-2 gap-10">
              <Skeleton className="aspect-square rounded-2xl" />
              <div className="space-y-4">
                <Skeleton className="h-8 w-2/3" />
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-24 w-full" />
              </div>
            </div>
          ) : notFound || !product ? (
            <div className="text-center py-20" data-testid="product-not-found">
              <p className="font-heading text-2xl text-foreground">This item isn&apos;t available anymore.</p>
              <p className="mt-2 text-muted-foreground">It may have been sold or removed.</p>
              <Button asChild variant="premium" className="mt-6">
                <Link to="/all-items">Browse All Items</Link>
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8 lg:gap-14" data-testid="product-detail">
              <MediaGallery product={product} className="aspect-square rounded-2xl w-full" />

              <div className="flex flex-col">
                <span className="inline-block w-fit rounded-full bg-primary/12 text-primary text-xs font-medium uppercase tracking-wide px-3 py-1 capitalize">
                  {product.category}
                </span>
                <h1 className="mt-3 font-heading text-2xl sm:text-3xl font-semibold text-foreground">
                  {product.name}
                </h1>

                {product.purity && (
                  <span className="mt-2 inline-block w-fit rounded-full bg-primary text-primary-foreground text-xs font-semibold px-3 py-1">
                    {product.purity}
                  </span>
                )}

                {product.tag && <p className="mt-4 text-muted-foreground">{product.tag}</p>}
                {product.description && (
                  <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{product.description}</p>
                )}

                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <Button asChild variant="whatsapp" size="lg" className="flex-1" data-testid="product-enquire-btn">
                    <a
                      href={buildWhatsAppLink(enquireMessage(product.name))}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fa-brands fa-whatsapp" /> Enquire on WhatsApp
                    </a>
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => {
                      handleShare();
                      toast.success("Opening share options...");
                    }}
                    data-testid="product-share-btn"
                  >
                    <Share2 className="h-4 w-4" /> Share
                  </Button>
                </div>

                <p className="mt-6 text-xs text-muted-foreground">
                  Ask us for current pricing, purity certification &amp; custom sizing on WhatsApp.
                </p>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
